import React, { useState, useEffect } from 'react';
import { useDraggable, DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { useSortable, SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDroppable } from '@dnd-kit/core';
import { GripVertical, X, Plus, Download, File as FileIcon, Pencil, MoreHorizontal, Copy, Trash2, User, Calendar, Lock, Shuffle, Zap, Hand, Search, FileText as FileTextIcon, Link as LinkIcon, Table as TableIcon, Asterisk, Check, PenLine, Loader2, AlertCircle, FlagTriangleRight, Variable, Image as ImageIcon, Video as VideoIcon, Code, Settings2, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from '@/components/ui/dropdown-menu';
import { ConditionalLogicBuilder } from './ConditionalLogicBuilder';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { AsyncCombobox } from '@/components/ui/async-combobox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { TasksAiEditor } from '@/ai-editor';
import { ImageUpload } from './ImageUpload';
import { VideoUpload } from './VideoUpload';
import { FileUpload } from './FileUpload';
import { EmailEditor } from './EmailEditor';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

// Check if a string contains a variable pattern like {{ something }}
function containsVariable(str) {
  return typeof str === 'string' && /\{\{.*?\}\}/.test(str);
}

// Variable placeholder shown in the editor when a URL contains {{ vars }}
function VariablePlaceholder({ icon: Icon, label, url, onChangeUrl, onClear }) {
  return (
    <div className="border rounded-lg p-4 bg-purple-50/50 border-purple-200">
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 bg-purple-100 rounded-lg">
          <Icon className="h-5 w-5 text-purple-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-purple-700">{label}</p>
          <p className="text-xs text-purple-500">Dynamic — resolves at runtime</p>
        </div>
        {onClear && (
          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={onClear}>
            Clear
          </Button>
        )}
      </div>
      <Input
        value={url}
        onChange={(e) => onChangeUrl(e.target.value)}
        className="h-8 text-sm font-mono bg-white border-purple-200"
        placeholder="URL or {{ variable }}"
      />
    </div>
  );
}

// File Name Editor Component
function FileNameEditor({ fileData, formatFileSize, handleDownload, onUpdateName, onChangeFile }) {
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(fileData.name);

  const handleNameSave = () => {
    if (editedName.trim() && editedName !== fileData.name) {
      onUpdateName(editedName.trim());
    }
    setIsEditingName(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleNameSave();
    } else if (e.key === 'Escape') {
      setEditedName(fileData.name);
      setIsEditingName(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="border rounded-lg p-4 bg-muted/30">
        <div className="flex items-center gap-3">
          <FileIcon className="h-8 w-8 text-muted-foreground flex-shrink-0" />
          <div className="flex-1 min-w-0">
            {isEditingName ? (
              <Input
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                onBlur={handleNameSave}
                onKeyDown={handleKeyDown}
                className="h-8 text-sm font-medium"
                autoFocus
              />
            ) : (
              <div
                className="font-medium truncate cursor-pointer hover:text-primary transition-colors"
                onClick={() => setIsEditingName(true)}
                title="Click to edit name"
              >
                {fileData.name}
              </div>
            )}
            {fileData.size && (
              <div className="text-xs text-muted-foreground">
                {formatFileSize(fileData.size)}
              </div>
            )}
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={handleDownload}
            title="Download file"
          >
            <Download className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <Button
        variant="outline"
        size="sm"
        className="w-full"
        onClick={onChangeFile}
      >
        Change File
      </Button>
    </div>
  );
}

// Sortable Option Item Component (for Dropdown/Multi Choice)
function SortableOptionItem({ option, index, editingOptionId, onUpdate, onRemove, onStartEdit, onStopEdit, isLast, onAddAfter }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: option.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Number / Drag Handle */}
      <div
        className="text-sm text-muted-foreground w-4 cursor-grab active:cursor-grabbing flex items-center justify-center"
        {...attributes}
        {...listeners}
      >
        {isHovered ? (
          <GripVertical className="h-4 w-4" />
        ) : (
          <span>{index + 1}</span>
        )}
      </div>
      {editingOptionId === option.id ? (
        <Input
          value={option.value}
          onChange={(e) => onUpdate(option.id, e.target.value)}
          onBlur={() => onStopEdit(option.id)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (isLast && option.value.trim() && onAddAfter) {
                onAddAfter();
              } else {
                onStopEdit(option.id);
              }
            }
          }}
          className="h-9 text-sm flex-1"
          placeholder="Option text..."
          autoFocus
        />
      ) : (
        <div
          className="flex-1 h-9 px-3 py-2 border rounded-md bg-background text-sm cursor-pointer hover:bg-muted/50 transition-colors flex items-center"
          onClick={() => onStartEdit(option.id)}
        >
          {option.value || 'Option ' + (index + 1)}
        </div>
      )}
      {isHovered && (
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9"
          onClick={() => onRemove(option.id)}
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

// Multi Choice Setup Component
function MultiChoiceSetup({ options = [], onChange }) {
  const [editingOptionId, setEditingOptionId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const addOption = () => {
    const newOption = { id: Date.now(), value: '' };
    const newOptions = [...options, newOption];
    onChange(newOptions);
    // Set this new option as editing immediately
    setEditingOptionId(newOption.id);
  };

  const removeOption = (optionId) => {
    const newOptions = options.filter((opt) => opt.id !== optionId);
    onChange(newOptions);
  };

  const updateOptionValue = (optionId, newValue) => {
    const newOptions = options.map((opt) =>
      opt.id === optionId ? { ...opt, value: newValue } : opt
    );
    onChange(newOptions);
  };

  const handleOptionBlur = (optionId) => {
    // If option value is empty after blur, remove the option
    const option = options.find((opt) => opt.id === optionId);
    if (option && !option.value.trim()) {
      removeOption(optionId);
    }
    setEditingOptionId(null);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = options.findIndex(opt => opt.id === active.id);
      const newIndex = options.findIndex(opt => opt.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedOptions = arrayMove(options, oldIndex, newIndex);
        onChange(reorderedOptions);
      }
    }
  };

  return (
    <div className="space-y-3">
      <div className="border rounded-lg p-3 bg-muted/10">
        <div className="text-sm font-medium mb-3">Multi Choice</div>
        {options.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={options.map(opt => opt.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {options.map((option, index) => (
                  <SortableOptionItem
                    key={option.id}
                    option={option}
                    index={index}
                    editingOptionId={editingOptionId}
                    onUpdate={updateOptionValue}
                    onRemove={removeOption}
                    onStartEdit={setEditingOptionId}
                    onStopEdit={handleOptionBlur}
                    isLast={index === options.length - 1}
                    onAddAfter={addOption}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <div className="text-xs text-muted-foreground text-center py-2">
            No options yet. Click + to add options.
          </div>
        )}
      </div>
      <Button
        variant="outline"
        size="sm"
        className="w-full gap-2"
        onClick={addOption}
      >
        <Plus className="h-4 w-4" />
        Add Option
      </Button>
    </div>
  );
}

// AiTables Setup Component - Used when AiTables integration is selected
// User-scoped API key: user must pick a database first
function AiTablesSetup({ content = {}, onChange, companyId }) {
  const [databases, setDatabases] = useState([]);
  const [tables, setTables] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loadingDatabases, setLoadingDatabases] = useState(false);
  const [loadingTables, setLoadingTables] = useState(false);
  const [loadingColumns, setLoadingColumns] = useState(false);
  const [savingDatabase, setSavingDatabase] = useState(false);
  const [error, setError] = useState(null);
  const [storedDatabaseId, setStoredDatabaseId] = useState(null);
  const [storedDatabaseName, setStoredDatabaseName] = useState(null);

  const aiTablesConfig = content.aiTablesConfig || {};

  // Load databases + check if one is already stored on the integration
  useEffect(() => {
    if (!companyId) return;
    const load = async () => {
      setLoadingDatabases(true);
      setError(null);
      try {
        const { getIntegrationStatus, getDatabases } = await import('@/lib/aitablesService');

        // Check if integration already has a database selected
        const status = await getIntegrationStatus(companyId);
        const savedDbId = status.integration?.databaseId;
        const savedDbName = status.integration?.databaseName;
        if (savedDbId) {
          setStoredDatabaseId(savedDbId);
          setStoredDatabaseName(savedDbName);
        }

        // Fetch available databases
        const dbs = await getDatabases(companyId);
        setDatabases(dbs);

        // If a database is already stored, load its tables immediately
        if (savedDbId) {
          await fetchTablesForDatabase(savedDbId);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoadingDatabases(false);
      }
    };
    load();
  }, [companyId]);

  const fetchTablesForDatabase = async (databaseId) => {
    if (!companyId || !databaseId) return;
    setLoadingTables(true);
    setError(null);
    try {
      const { getTables } = await import('@/lib/aitablesService');
      const tablesData = await getTables(companyId, databaseId);
      setTables(tablesData);
    } catch (err) {
      setError(err.message);
      setTables([]);
    } finally {
      setLoadingTables(false);
    }
  };

  const handleSelectDatabase = async (databaseId) => {
    if (!databaseId) return;
    const db = databases.find(d => d.id === databaseId);
    if (!db) return;

    // Immediately clear stale tables/columns
    setTables([]);
    setColumns([]);
    setSavingDatabase(true);
    setError(null);
    try {
      const { setDatabase } = await import('@/lib/aitablesService');
      await setDatabase(companyId, db.id, db.name);
      setStoredDatabaseId(db.id);
      setStoredDatabaseName(db.name);

      // Fetch tables for the new database first
      await fetchTablesForDatabase(db.id);

      // Then update parent config (clear previous table/column selection)
      onChange({
        ...content,
        aiTablesConfig: {
          ...aiTablesConfig,
          databaseId: db.id,
          databaseName: db.name,
          tableId: null,
          tableName: null,
          valueColumn: null,
          labelColumn: null,
        }
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingDatabase(false);
    }
  };

  const fetchColumns = async (tableId) => {
    if (!companyId || !tableId) return;
    setLoadingColumns(true);
    setError(null);
    try {
      const { getTableColumns } = await import('@/lib/aitablesService');
      const data = await getTableColumns(companyId, tableId);
      setColumns(data);
    } catch (err) {
      setError(err.message);
      setColumns([]);
    } finally {
      setLoadingColumns(false);
    }
  };

  // Load columns when table changes
  useEffect(() => {
    if (aiTablesConfig.tableId) {
      fetchColumns(aiTablesConfig.tableId);
    } else {
      setColumns([]);
    }
  }, [aiTablesConfig.tableId]);

  const updateConfig = (field, value) => {
    const newConfig = { ...aiTablesConfig, [field]: value };

    // Clear dependent fields when table changes
    if (field === 'tableId') {
      newConfig.valueColumn = null;
      newConfig.labelColumn = null;
      const tbl = tables.find(t => t.id === value);
      newConfig.tableName = tbl?.name || null;
    }

    onChange({ ...content, aiTablesConfig: newConfig });
  };

  const activeDatabaseId = storedDatabaseId || aiTablesConfig.databaseId;

  return (
    <div className="border rounded-lg p-3 bg-muted/10 space-y-3">
      <div className="flex items-center gap-2">
        <div className="w-6 h-6 rounded bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
          <TableIcon className="h-3.5 w-3.5 text-white" />
        </div>
        <div className="text-sm font-medium">AiTables</div>
      </div>

      {error && (
        <div className="p-2 border border-destructive/50 rounded-md bg-destructive/10">
          <p className="text-xs text-destructive">{error}</p>
        </div>
      )}

      {/* Database Selection */}
      <div className="space-y-2">
        <label className="text-xs text-muted-foreground">Database</label>
        <select
          value={activeDatabaseId || ''}
          onChange={(e) => handleSelectDatabase(e.target.value)}
          className="w-full h-9 px-3 text-sm border rounded-md bg-background"
          disabled={loadingDatabases || savingDatabase}
        >
          <option value="">
            {loadingDatabases ? 'Loading databases...' : savingDatabase ? 'Saving...' : 'Select a database'}
          </option>
          {databases.map((db) => (
            <option key={db.id} value={db.id}>
              {db.name}
            </option>
          ))}
        </select>
      </div>

      {/* Table Selection — only after database is selected */}
      {activeDatabaseId && (
        <div className="space-y-2">
          <label className="text-xs text-muted-foreground">Table</label>
          <select
            value={aiTablesConfig.tableId || ''}
            onChange={(e) => updateConfig('tableId', e.target.value || null)}
            className="w-full h-9 px-3 text-sm border rounded-md bg-background"
            disabled={loadingTables}
          >
            <option value="">
              {loadingTables ? 'Loading tables...' : 'Select a table'}
            </option>
            {tables.map((tbl) => (
              <option key={tbl.id} value={tbl.id}>
                {tbl.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Column Selection */}
      {aiTablesConfig.tableId && (
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-2">
            <label className="text-xs text-muted-foreground">Value Column</label>
            <select
              value={aiTablesConfig.valueColumn || ''}
              onChange={(e) => updateConfig('valueColumn', e.target.value || null)}
              className="w-full h-9 px-3 text-sm border rounded-md bg-background"
              disabled={loadingColumns}
            >
              <option value="">
                {loadingColumns ? 'Loading...' : 'Select column'}
              </option>
              {columns.map((col) => (
                <option key={col.id} value={col.name}>
                  {col.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <label className="text-xs text-muted-foreground">Label Column</label>
            <select
              value={aiTablesConfig.labelColumn || ''}
              onChange={(e) => updateConfig('labelColumn', e.target.value || null)}
              className="w-full h-9 px-3 text-sm border rounded-md bg-background"
              disabled={loadingColumns}
            >
              <option value="">
                {loadingColumns ? 'Loading...' : 'Select column'}
              </option>
              {columns.map((col) => (
                <option key={col.id} value={col.name}>
                  {col.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Summary when fully configured */}
      {aiTablesConfig.tableId && aiTablesConfig.valueColumn && aiTablesConfig.labelColumn && (
        <div className="p-2 bg-green-50 border border-green-200 rounded-md">
          <p className="text-xs text-green-700">
            Options will be populated from <strong>{aiTablesConfig.tableName}</strong> using <strong>{aiTablesConfig.labelColumn}</strong> as the label
          </p>
        </div>
      )}
    </div>
  );
}

// Dropdown Setup Component
function DropdownSetup({ content = {}, onChange, companyId }) {
  const [editingOptionId, setEditingOptionId] = useState(null);
  const [aiTablesConfigured, setAiTablesConfigured] = useState(null);
  const [checkingAiTables, setCheckingAiTables] = useState(false);
  const [showApiKeyInput, setShowApiKeyInput] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [workspaceNameInput, setWorkspaceNameInput] = useState('');
  const [savingApiKey, setSavingApiKey] = useState(false);
  const [apiKeyError, setApiKeyError] = useState(null);

  const options = content.options || [];
  const dataSourceType = content.dataSourceType || 'static'; // 'static', 'dynamic', or 'aitables'
  const dataSource = content.dataSource || {};

  // Check if AiTables is configured when selecting that option
  useEffect(() => {
    if (dataSourceType === 'aitables' && companyId && aiTablesConfigured === null) {
      checkAiTablesIntegration();
    }
  }, [dataSourceType, companyId]);

  const checkAiTablesIntegration = async () => {
    if (!companyId) return;
    setCheckingAiTables(true);
    try {
      const { getIntegrationStatus } = await import('@/lib/aitablesService');
      const result = await getIntegrationStatus(companyId);
      setAiTablesConfigured(result.isConfigured);
    } catch (err) {
      console.error('Error checking AiTables integration:', err);
      setAiTablesConfigured(false);
    } finally {
      setCheckingAiTables(false);
    }
  };

  const handleSaveApiKey = async () => {
    if (!apiKeyInput.trim()) {
      setApiKeyError('Please enter an API key');
      return;
    }

    setSavingApiKey(true);
    setApiKeyError(null);
    try {
      const { validateAndSaveApiKey } = await import('@/lib/aitablesService');
      const result = await validateAndSaveApiKey(companyId, apiKeyInput.trim(), workspaceNameInput.trim());

      if (result.valid) {
        setAiTablesConfigured(true);
        setShowApiKeyInput(false);
        setApiKeyInput('');
        setWorkspaceNameInput('');
      } else {
        setApiKeyError(result.error || 'Invalid API key');
      }
    } catch (err) {
      setApiKeyError(err.message);
    } finally {
      setSavingApiKey(false);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = options.findIndex(opt => opt.id === active.id);
      const newIndex = options.findIndex(opt => opt.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedOptions = arrayMove(options, oldIndex, newIndex);
        onChange({ ...content, options: reorderedOptions });
      }
    }
  };

  const addOption = () => {
    const newOption = { id: Date.now(), value: '' };
    const newOptions = [...options, newOption];
    onChange({ ...content, options: newOptions });
    setEditingOptionId(newOption.id);
  };

  const removeOption = (optionId) => {
    const newOptions = options.filter((opt) => opt.id !== optionId);
    onChange({ ...content, options: newOptions });
  };

  const updateOptionValue = (optionId, newValue) => {
    const newOptions = options.map((opt) =>
      opt.id === optionId ? { ...opt, value: newValue } : opt
    );
    onChange({ ...content, options: newOptions });
  };

  const handleOptionBlur = (optionId) => {
    const option = options.find((opt) => opt.id === optionId);
    if (option && !option.value.trim()) {
      removeOption(optionId);
    }
    setEditingOptionId(null);
  };

  const updateDataSourceType = (type) => {
    onChange({ ...content, dataSourceType: type });
  };

  const updateDataSource = (field, value) => {
    onChange({
      ...content,
      dataSource: { ...dataSource, [field]: value }
    });
  };

  return (
    <div className="space-y-3">
      {/* Data Source Type Toggle */}
      <div className="flex gap-1 p-1 bg-muted rounded-lg">
        <button
          onClick={() => updateDataSourceType('static')}
          className={cn(
            'flex-1 text-xs font-medium py-1.5 px-2 rounded-md transition-colors',
            dataSourceType === 'static'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          Static
        </button>
        <button
          onClick={() => updateDataSourceType('dynamic')}
          className={cn(
            'flex-1 text-xs font-medium py-1.5 px-2 rounded-md transition-colors',
            dataSourceType === 'dynamic'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          API
        </button>
        <button
          onClick={() => updateDataSourceType('aitables')}
          className={cn(
            'flex-1 text-xs font-medium py-1.5 px-2 rounded-md transition-colors',
            dataSourceType === 'aitables'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          AiTables
        </button>
      </div>

      {dataSourceType === 'static' ? (
        /* Static Options Editor */
        <>
          <div className="border rounded-lg p-3 bg-muted/10">
            <div className="text-sm font-medium mb-3">Options</div>
            {options.length > 0 ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={options.map(opt => opt.id)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-2">
                    {options.map((option, index) => (
                      <SortableOptionItem
                        key={option.id}
                        option={option}
                        index={index}
                        editingOptionId={editingOptionId}
                        onUpdate={updateOptionValue}
                        onRemove={removeOption}
                        onStartEdit={setEditingOptionId}
                        onStopEdit={handleOptionBlur}
                        isLast={index === options.length - 1}
                        onAddAfter={addOption}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            ) : (
              <div className="text-xs text-muted-foreground text-center py-2">
                No options yet. Click + to add options.
              </div>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full gap-2"
            onClick={addOption}
          >
            <Plus className="h-4 w-4" />
            Add Option
          </Button>
        </>
      ) : dataSourceType === 'dynamic' ? (
        /* Dynamic Data Source Configuration */
        <div className="border rounded-lg p-3 bg-muted/10 space-y-3">
          <div className="text-sm font-medium">API Data Source</div>
          <div className="space-y-2">
            <label className="text-xs text-muted-foreground">Endpoint URL</label>
            <Input
              value={dataSource.url || ''}
              onChange={(e) => updateDataSource('url', e.target.value)}
              placeholder="https://api.example.com/options"
              className="text-sm"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-2">
              <label className="text-xs text-muted-foreground">Value Field</label>
              <Input
                value={dataSource.valueField || ''}
                onChange={(e) => updateDataSource('valueField', e.target.value)}
                placeholder="id"
                className="text-sm"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs text-muted-foreground">Label Field</label>
              <Input
                value={dataSource.labelField || ''}
                onChange={(e) => updateDataSource('labelField', e.target.value)}
                placeholder="name"
                className="text-sm"
              />
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-xs text-muted-foreground">Data Path (optional)</label>
            <Input
              value={dataSource.dataPath || ''}
              onChange={(e) => updateDataSource('dataPath', e.target.value)}
              placeholder="data.items"
              className="text-sm"
            />
            <p className="text-[10px] text-muted-foreground">
              Path to array in response (e.g., "data.items" or leave empty for root array)
            </p>
          </div>
        </div>
      ) : (
        /* AiTables Configuration */
        <>
          {checkingAiTables ? (
            <div className="border rounded-lg p-4 bg-muted/10 text-center">
              <p className="text-sm text-muted-foreground">Checking AiTables integration...</p>
            </div>
          ) : aiTablesConfigured ? (
            <AiTablesSetup
              content={content}
              onChange={onChange}
              companyId={companyId}
            />
          ) : showApiKeyInput ? (
            /* API Key Setup Form */
            <div className="border rounded-lg p-3 bg-muted/10 space-y-3">
              <div className="text-sm font-medium">Connect AiTables</div>
              <div className="space-y-2">
                <label className="text-xs text-muted-foreground">API Key</label>
                <Input
                  type="password"
                  value={apiKeyInput}
                  onChange={(e) => setApiKeyInput(e.target.value)}
                  placeholder="ait_live_..."
                  className="text-sm font-mono"
                />
              </div>
              <div className="space-y-2">
                <label className="text-xs text-muted-foreground">Workspace Name (optional)</label>
                <Input
                  value={workspaceNameInput}
                  onChange={(e) => setWorkspaceNameInput(e.target.value)}
                  placeholder="My Workspace"
                  className="text-sm"
                />
              </div>
              {apiKeyError && (
                <div className="p-2 border border-destructive/50 rounded-md bg-destructive/10">
                  <p className="text-xs text-destructive">{apiKeyError}</p>
                </div>
              )}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => setShowApiKeyInput(false)}
                  disabled={savingApiKey}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  className="flex-1"
                  onClick={handleSaveApiKey}
                  disabled={savingApiKey}
                >
                  {savingApiKey ? 'Connecting...' : 'Connect'}
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground text-center">
                Get your API key from AiTables: Database &gt; API Keys
              </p>
            </div>
          ) : (
            /* Setup Prompt */
            <div className="border rounded-lg p-4 bg-muted/10 text-center space-y-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center mx-auto">
                <TableIcon className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium">Connect AiTables</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Link your AiTables workspace to populate dropdowns from your databases
                </p>
              </div>
              <Button
                size="sm"
                className="w-full"
                onClick={() => setShowApiKeyInput(true)}
              >
                Set Up Integration
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Table Setup Component
function TableSetup({ columns = [], onChange }) {
  const [editingColumnId, setEditingColumnId] = useState(null);

  const addColumn = () => {
    const newColumn = { id: Date.now(), name: '' };
    const newColumns = [...columns, newColumn];
    onChange(newColumns);
    // Set this new column as editing immediately
    setEditingColumnId(newColumn.id);
  };

  const removeColumn = (columnId) => {
    const newColumns = columns.filter((col) => col.id !== columnId);
    onChange(newColumns);
  };

  const updateColumnName = (columnId, newName) => {
    const newColumns = columns.map((col) =>
      col.id === columnId ? { ...col, name: newName } : col
    );
    onChange(newColumns);
  };

  const handleColumnBlur = (columnId) => {
    // If column name is empty after blur, remove the column
    const column = columns.find((col) => col.id === columnId);
    if (column && !column.name.trim()) {
      removeColumn(columnId);
    }
    setEditingColumnId(null);
  };

  return (
    <div className="space-y-3">
      <table className="w-full border-collapse border border-gray-300">
        <thead>
          <tr className="bg-muted/30">
            {columns.length > 0 ? (
              columns.map((column) => (
                <th key={column.id} className="border border-gray-300 p-2 text-left text-sm font-medium relative group">
                  <div className="flex items-center justify-between gap-2">
                    {editingColumnId === column.id ? (
                      <Input
                        value={column.name}
                        onChange={(e) => updateColumnName(column.id, e.target.value)}
                        onBlur={() => handleColumnBlur(column.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            setEditingColumnId(null);
                          }
                        }}
                        className="h-7 text-sm font-medium"
                        placeholder="Column name..."
                        autoFocus
                      />
                    ) : (
                      <span
                        className="flex-1 cursor-pointer hover:text-primary transition-colors"
                        onClick={() => setEditingColumnId(column.id)}
                      >
                        {column.name || 'Unnamed Column'}
                      </span>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => removeColumn(column.id)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                </th>
              ))
            ) : (
              <th className="border border-gray-300 p-2 text-left text-sm text-muted-foreground">
                Click + to add columns
              </th>
            )}
            <th className="border border-gray-300 p-2 w-12 text-center">
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 mx-auto"
                onClick={addColumn}
              >
                <Plus className="h-4 w-4 text-muted-foreground" />
              </Button>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td colSpan={columns.length || 1} className="border border-gray-300 p-2 text-sm text-muted-foreground">
              Data will be typed here...
            </td>
            <td className="border border-gray-300 p-2"></td>
          </tr>
        </tbody>
      </table>

      {columns.length > 0 && (
        <div className="text-xs text-muted-foreground">
          Users will be able to add rows and fill in data for these columns when running the workflow.
        </div>
      )}
    </div>
  );
}

// Embed Block Component
function EmbedBlock({ data, onChange }) {
  const [url, setUrl] = useState(data.url || '');
  const [inputUrl, setInputUrl] = useState(data.url || '');
  const [isEditing, setIsEditing] = useState(!data.url);

  const handleSubmit = () => {
    if (inputUrl.trim()) {
      setUrl(inputUrl.trim());
      onChange({ url: inputUrl.trim() });
      setIsEditing(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  if (!url || isEditing) {
    return (
      <div className="border-2 border-primary/20 rounded-lg p-8 bg-muted/5">
        <div className="flex items-center gap-3 max-w-2xl mx-auto">
          <LinkIcon className="h-5 w-5 text-muted-foreground flex-shrink-0" />
          <Input
            value={inputUrl}
            onChange={(e) => setInputUrl(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Paste URL or {{ variable }}"
            className="flex-1"
            autoFocus
          />
          <Button onClick={handleSubmit} disabled={!inputUrl.trim()}>
            Embed
          </Button>
        </div>
      </div>
    );
  }

  // Variable URL — show clean placeholder, don't try to render iframe
  if (containsVariable(url)) {
    return (
      <VariablePlaceholder
        icon={Code}
        label="Dynamic Embed"
        url={url}
        onChangeUrl={(newUrl) => {
          setUrl(newUrl);
          setInputUrl(newUrl);
          onChange({ url: newUrl });
        }}
        onClear={() => {
          setUrl('');
          setInputUrl('');
          onChange({ url: '' });
          setIsEditing(true);
        }}
      />
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden bg-muted/5">
      <div className="flex items-center justify-between px-4 py-2 bg-muted/30 border-b">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <LinkIcon className="h-4 w-4" />
          <span className="truncate max-w-md">{url}</span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsEditing(true)}
        >
          Edit URL
        </Button>
      </div>
      <div className="relative w-full h-[600px] bg-white">
        <iframe
          src={url}
          className="w-full h-full border-0"
          title="Embedded content"
          sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
          loading="lazy"
        />
      </div>
    </div>
  );
}

// Page Selector Component
function PageSelector({ selectedPage, onSelectPage, projectId }) {
  const [pages, setPages] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchPages();
  }, [projectId]);

  const fetchPages = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('pages')
        .select('id, name, updated_date')
        .eq('project_id', projectId)
        .order('updated_date', { ascending: false });

      if (error) throw error;
      setPages(data || []);
    } catch (error) {
      console.error('Error fetching pages:', error);
      toast.error('Failed to load pages');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredPages = pages.filter(page =>
    page.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // If a page is already selected, show it
  if (selectedPage) {
    return (
      <div className="border rounded-lg p-4 bg-muted/10">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center flex-shrink-0">
            <FileTextIcon className="h-6 w-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium truncate">{selectedPage.name}</div>
            <div className="text-xs text-muted-foreground">
              Last modified {new Date(selectedPage.updated_date).toLocaleDateString()}
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onSelectPage(null)}
          >
            Change
          </Button>
        </div>
      </div>
    );
  }

  // Show page selector
  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          type="text"
          placeholder="Search pages..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      {isLoading ? (
        <div className="text-center py-8 text-sm text-muted-foreground">
          Loading pages...
        </div>
      ) : filteredPages.length === 0 ? (
        <div className="text-center py-8 text-sm text-muted-foreground">
          {searchQuery ? 'No pages found' : 'No pages available'}
        </div>
      ) : (
        <div className="border rounded-lg divide-y max-h-[300px] overflow-y-auto">
          {filteredPages.map((page) => (
            <button
              key={page.id}
              onClick={() => onSelectPage(page)}
              className="w-full flex items-center gap-3 p-3 hover:bg-muted/50 transition-colors text-left"
            >
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center flex-shrink-0">
                <FileTextIcon className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{page.name}</div>
                <div className="text-xs text-muted-foreground">
                  {new Date(page.updated_date).toLocaleDateString()}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Sortable Subtask Item Component
function SortableSubtaskItem({ subtask, index, onUpdate, onRemove }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: subtask.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Number / Drag Handle */}
      <div
        className="text-sm text-muted-foreground w-6 cursor-grab active:cursor-grabbing flex items-center justify-center"
        {...attributes}
        {...listeners}
      >
        {isHovered ? (
          <GripVertical className="h-4 w-4" />
        ) : (
          <span>{index + 1}</span>
        )}
      </div>
      <Checkbox disabled />
      <Input
        value={subtask.text}
        onChange={(e) => onUpdate(subtask.id, e.target.value)}
        className="flex-1 h-9"
        placeholder="Subtask description"
      />
      {isHovered && (
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9"
          onClick={() => onRemove(subtask.id)}
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

// Subtasks Editor Component
function SubtasksEditor({ subtasks = [], onUpdate }) {
  const [newSubtaskText, setNewSubtaskText] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const addSubtask = () => {
    if (newSubtaskText.trim()) {
      const updatedSubtasks = [...subtasks, { id: Date.now(), text: newSubtaskText.trim(), completed: false }];
      onUpdate(updatedSubtasks);
      setNewSubtaskText('');
    }
  };

  const removeSubtask = (subtaskId) => {
    const updatedSubtasks = subtasks.filter(st => st.id !== subtaskId);
    onUpdate(updatedSubtasks);
  };

  const updateSubtask = (subtaskId, text) => {
    const updatedSubtasks = subtasks.map(st => st.id === subtaskId ? { ...st, text } : st);
    onUpdate(updatedSubtasks);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = subtasks.findIndex(st => st.id === active.id);
      const newIndex = subtasks.findIndex(st => st.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedSubtasks = arrayMove(subtasks, oldIndex, newIndex);
        onUpdate(reorderedSubtasks);
      }
    }
  };

  return (
    <div className="space-y-3">
      {subtasks.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={subtasks.map(st => st.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {subtasks.map((subtask, index) => (
                <SortableSubtaskItem
                  key={subtask.id}
                  subtask={subtask}
                  index={index}
                  onUpdate={updateSubtask}
                  onRemove={removeSubtask}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
      <div className="flex gap-2">
        <Input
          value={newSubtaskText}
          onChange={(e) => setNewSubtaskText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addSubtask();
            }
          }}
          placeholder="Add a subtask"
          className="flex-1 h-9"
        />
        <Button
          variant="outline"
          size="sm"
          className="gap-2 h-9"
          onClick={addSubtask}
          disabled={!newSubtaskText.trim()}
        >
          <Plus className="h-4 w-4" />
          Add
        </Button>
      </div>
    </div>
  );
}

// Placeholder drop zone between items
export function DropPlaceholder({ index, isOver }) {
  return (
    <div
      className={`h-12 flex items-center justify-center rounded-lg border-2 border-dashed transition-all duration-200 ${
        isOver
          ? 'border-primary bg-primary/5 scale-105'
          : 'border-muted/40 bg-muted/20 opacity-50 hover:opacity-100 hover:border-muted'
      }`}
    >
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Plus className="h-3 w-3" />
        <span>Drag new field here</span>
      </div>
    </div>
  );
}

// Draggable item from sidebar
export function DraggableFieldItem({ item, category }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `draggable-${category}-${item.label}`,
    data: {
      type: 'new-item',
      itemType: item.label,
      label: item.label,
      icon: item.icon,
      color: item.color,
      category: category,
    },
  });

  const Icon = item.icon;

  return (
    <button
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={`w-full flex items-center gap-2 px-3 py-2 rounded-md hover:bg-muted text-sm transition-colors cursor-grab active:cursor-grabbing ${
        isDragging ? 'opacity-30' : ''
      }`}
    >
      <Icon className={`h-4 w-4 ${item.color}`} />
      {item.label}
    </button>
  );
}

// Sortable item in canvas
export function SortableCanvasItem({ item, onRemove, onDuplicate, onUpdateContent, projectId, taskId, companyId, showDropIndicator, dropIndicatorPosition = 'below', tasks, currentTaskId, onUpdateConditionalLogic }) {
  const [showConditionBuilder, setShowConditionBuilder] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.id,
    data: {
      type: 'canvas-item',
      item,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || 'transform 200ms ease',
    opacity: isDragging ? 0.3 : 1,
  };

  const Icon = item.icon;

  // Check if field has active conditional logic
  const hasConditions = item.conditionalLogic &&
    item.conditionalLogic.mode !== 'disabled' &&
    ((item.conditionalLogic.conditions?.length > 0 && item.conditionalLogic.conditions.some(c => c.fieldId)) ||
     (item.conditionalLogic.groups?.length > 0));

  // Handle conditional logic mode change
  const handleConditionModeChange = (mode) => {
    if (mode === 'disabled') {
      // Clear conditions when disabling
      if (onUpdateConditionalLogic) {
        onUpdateConditionalLogic(item.id, { mode: 'disabled', conditions: [], groups: [], matchType: 'all' });
      }
    } else {
      // Show builder for show_when or hide_when
      if (onUpdateConditionalLogic) {
        const currentLogic = item.conditionalLogic || {};
        onUpdateConditionalLogic(item.id, { ...currentLogic, mode });
      }
      setShowConditionBuilder(true);
    }
  };

  // Handle save from condition builder
  const handleSaveConditions = (conditionalLogic) => {
    if (onUpdateConditionalLogic) {
      onUpdateConditionalLogic(item.id, conditionalLogic);
    }
  };

  // Render appropriate content editor based on item type
  const renderContentEditor = () => {
    // Content items (for styling the form)
    if (item.category === 'content') {
      switch (item.label) {
        case 'Text':
          return (
            <div className="relative group/editor">
              <style>{`
                .text-editor-wrapper .tiptap-toolbar,
                .text-editor-wrapper [role="toolbar"],
                .text-editor-wrapper .editor-toolbar,
                .text-editor-wrapper .formatting-toolbar,
                .text-editor-wrapper > div:first-child > div:first-child {
                  opacity: 0;
                  transition: opacity 0.2s;
                  position: absolute;
                  top: -70px;
                  left: 0;
                  right: 0;
                  z-index: 50;
                  background: white;
                  border-radius: 8px;
                  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                  padding: 4px;
                  pointer-events: none;
                }
                .text-editor-wrapper:hover .tiptap-toolbar,
                .text-editor-wrapper:hover [role="toolbar"],
                .text-editor-wrapper:hover .editor-toolbar,
                .text-editor-wrapper:hover .formatting-toolbar,
                .text-editor-wrapper:hover > div:first-child > div:first-child,
                .text-editor-wrapper .tiptap-toolbar:hover,
                .text-editor-wrapper [role="toolbar"]:hover,
                .text-editor-wrapper .editor-toolbar:hover,
                .text-editor-wrapper .formatting-toolbar:hover,
                .text-editor-wrapper > div:first-child > div:first-child:hover {
                  opacity: 1;
                  pointer-events: auto;
                }
                .text-editor-wrapper .ProseMirror,
                .text-editor-wrapper .tiptap,
                .text-editor-wrapper > div {
                  border: 0 !important;
                  min-height: auto !important;
                  padding: 0 !important;
                }
                .text-editor-wrapper .ProseMirror {
                  min-height: 1.5em !important;
                }
                .text-editor-wrapper .ProseMirror p.is-editor-empty:first-child::before {
                  color: #9ca3af;
                  content: attr(data-placeholder);
                  float: left;
                  height: 0;
                  pointer-events: none;
                }
              `}</style>
              <div className="text-editor-wrapper">
                <TasksAiEditor
                  value={item.content}
                  onChange={(content) => onUpdateContent(item.id, content)}
                  placeholder="Write something"
                />
              </div>
            </div>
          );

        case 'Page':
          return (
            <PageSelector
              selectedPage={item.content}
              onSelectPage={(page) => onUpdateContent(item.id, page)}
              projectId={projectId}
            />
          );

        case 'Image':
          // If image is already uploaded, show it with option to change
          if (item.content) {
            const imageData = typeof item.content === 'string'
              ? { url: item.content, description: '' }
              : item.content;

            // Variable URL — show editable placeholder instead of broken image
            if (containsVariable(imageData.url)) {
              return (
                <VariablePlaceholder
                  icon={ImageIcon}
                  label="Dynamic Image"
                  url={imageData.url}
                  onChangeUrl={(newUrl) => onUpdateContent(item.id, { ...imageData, url: newUrl })}
                  onClear={() => onUpdateContent(item.id, null)}
                />
              );
            }

            return (
              <div className="space-y-3">
                <div className="relative border rounded-lg overflow-hidden group/media">
                  <img
                    src={imageData.url}
                    alt="Uploaded content"
                    className="w-full h-auto object-contain"
                  />
                  <Button
                    variant="secondary"
                    size="sm"
                    className="absolute top-2 right-2 opacity-0 group-hover/media:opacity-100 transition-opacity"
                    onClick={() => onUpdateContent(item.id, null)}
                  >
                    Change Image
                  </Button>
                </div>
                <Input
                  placeholder="Add description (optional)"
                  value={imageData.description || ''}
                  onChange={(e) => onUpdateContent(item.id, { ...imageData, description: e.target.value })}
                  className="text-sm italic text-muted-foreground border-0 px-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              </div>
            );
          }
          // Show upload interface if no image
          return (
            <ImageUpload
              currentImage={null}
              onImageSelected={(imageUrl) => onUpdateContent(item.id, { url: imageUrl, description: '' })}
              folderPath={`spaces/${projectId}/Tasks/${taskId}/images`}
            />
          );

        case 'Video':
          // If video is already uploaded, show it with option to change
          if (item.content) {
            const videoData = typeof item.content === 'string'
              ? { url: item.content, description: '' }
              : item.content;

            // Variable URL — show editable placeholder instead of broken video
            if (containsVariable(videoData.url)) {
              return (
                <VariablePlaceholder
                  icon={VideoIcon}
                  label="Dynamic Video"
                  url={videoData.url}
                  onChangeUrl={(newUrl) => onUpdateContent(item.id, { ...videoData, url: newUrl })}
                  onClear={() => onUpdateContent(item.id, null)}
                />
              );
            }

            const isEmbedUrl = videoData.url.includes('youtube.com/embed') ||
                               videoData.url.includes('youtu.be') ||
                               videoData.url.includes('vimeo.com');

            return (
              <div className="space-y-3">
                <div className="relative border rounded-lg overflow-hidden bg-black group/media">
                  {isEmbedUrl ? (
                    <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                      <iframe
                        src={videoData.url}
                        className="absolute top-0 left-0 w-full h-full"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title="Video content"
                      />
                    </div>
                  ) : (
                    <video
                      src={videoData.url}
                      controls
                      className="w-full h-auto"
                    >
                      Your browser does not support the video tag.
                    </video>
                  )}
                  <Button
                    variant="secondary"
                    size="sm"
                    className="absolute top-2 right-2 opacity-0 group-hover/media:opacity-100 transition-opacity z-10"
                    onClick={() => onUpdateContent(item.id, null)}
                  >
                    Change Video
                  </Button>
                </div>
                <Input
                  placeholder="Add description (optional)"
                  value={videoData.description || ''}
                  onChange={(e) => onUpdateContent(item.id, { ...videoData, description: e.target.value })}
                  className="text-sm italic text-muted-foreground border-0 px-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              </div>
            );
          }
          // Show upload interface if no video
          return (
            <VideoUpload
              currentVideo={null}
              onVideoSelected={(videoUrl) => onUpdateContent(item.id, { url: videoUrl, description: '' })}
              folderPath={`spaces/${projectId}/Tasks/${taskId}/videos`}
            />
          );

        case 'File':
          // If file is already uploaded, show it with download option
          if (item.content) {
            const fileData = typeof item.content === 'string'
              ? { url: item.content, name: 'File', size: null }
              : item.content;

            // Variable URL — show editable placeholder with name + URL
            if (containsVariable(fileData.url) || containsVariable(fileData.name)) {
              return (
                <div className="border rounded-lg p-4 bg-purple-50/50 border-purple-200">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <FileIcon className="h-5 w-5 text-purple-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-purple-700">Dynamic File</p>
                      <p className="text-xs text-purple-500">Resolves at runtime</p>
                    </div>
                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => onUpdateContent(item.id, null)}>
                      Clear
                    </Button>
                  </div>
                  <div className="space-y-2">
                    <Input
                      value={fileData.url || ''}
                      onChange={(e) => onUpdateContent(item.id, { ...fileData, url: e.target.value })}
                      className="h-8 text-sm font-mono bg-white border-purple-200"
                      placeholder="URL or {{ variable }}"
                    />
                    <Input
                      value={fileData.name || ''}
                      onChange={(e) => onUpdateContent(item.id, { ...fileData, name: e.target.value })}
                      className="h-8 text-sm bg-white border-purple-200"
                      placeholder="Display name"
                    />
                  </div>
                </div>
              );
            }

            const formatFileSize = (bytes) => {
              if (!bytes) return '';
              if (bytes < 1024) return bytes + ' B';
              if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
              return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
            };

            const handleDownload = () => {
              // Create a temporary link element and trigger download
              const link = document.createElement('a');
              link.href = fileData.url;
              link.download = fileData.name || 'download';
              link.target = '_blank';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            };

            return (
              <FileNameEditor
                fileData={fileData}
                formatFileSize={formatFileSize}
                handleDownload={handleDownload}
                onUpdateName={(newName) => {
                  const updatedFileData = { ...fileData, name: newName };
                  onUpdateContent(item.id, updatedFileData);
                }}
                onChangeFile={() => onUpdateContent(item.id, null)}
              />
            );
          }
          // Show upload interface if no file
          return (
            <FileUpload
              currentFile={null}
              onFileSelected={(fileData) => onUpdateContent(item.id, fileData)}
              folderPath={`spaces/${projectId}/Tasks/${taskId}/files`}
            />
          );

        case 'Email':
        case 'Send Email':
          return (
            <EmailEditor
              emailData={item.content}
              onUpdate={(updatedEmail) => onUpdateContent(item.id, updatedEmail)}
            />
          );

        case 'Subtasks':
          return (
            <SubtasksEditor
              subtasks={item.content || []}
              onUpdate={(updatedSubtasks) => onUpdateContent(item.id, updatedSubtasks)}
            />
          );

        case 'Embed':
          return (
            <EmbedBlock
              data={item.content || {}}
              onChange={(embedData) => onUpdateContent(item.id, embedData)}
            />
          );

        default:
          return (
            <div className="border-2 border-dashed rounded-md p-4 text-center text-sm text-muted-foreground">
              {item.label} content (coming soon)
            </div>
          );
      }
    }

    // Form fields (placeholders for user input when running workflow)
    if (item.category === 'form') {
      const fieldName = item.content?.fieldName || item.label;

      // Special handling for Table
      if (item.label === 'Table') {
        return (
          <TableSetup
            columns={item.content?.columns || []}
            onChange={(columns) => onUpdateContent(item.id, { ...item.content, columns })}
          />
        );
      }

      // Special handling for Dropdown
      if (item.label === 'Dropdown') {
        const dropdownContent = item.content || {};
        const sourceType = dropdownContent.dataSourceType || 'static';
        const optionCount = dropdownContent.options?.length || 0;
        const sourceLabel = sourceType === 'static'
          ? (optionCount > 0 ? `${optionCount} option${optionCount !== 1 ? 's' : ''}` : 'No options')
          : sourceType === 'aitables' ? 'AiTables' : 'API';

        return (
          <div className="flex items-center gap-2">
            <div className="flex-1 flex items-center h-9 px-3 border rounded-md bg-muted/50 text-sm text-muted-foreground">
              <span className="flex-1 truncate">Select an option...</span>
              <span className="text-[10px] bg-muted px-1.5 py-0.5 rounded flex-shrink-0">{sourceLabel}</span>
              <ChevronDown className="h-4 w-4 flex-shrink-0 ml-1.5 opacity-50" />
            </div>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="icon" className="h-9 w-9 flex-shrink-0">
                  <Settings2 className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-96 p-4" align="end">
                <DropdownSetup
                  content={dropdownContent}
                  onChange={(newContent) => onUpdateContent(item.id, newContent)}
                  companyId={companyId}
                />
              </PopoverContent>
            </Popover>
          </div>
        );
      }

      // Special handling for Multi Choice
      if (item.label === 'Multi Choice') {
        return (
          <MultiChoiceSetup
            options={item.content?.options || []}
            onChange={(options) => onUpdateContent(item.id, { ...item.content, options })}
          />
        );
      }

      // Special handling for Signature
      if (item.label === 'Signature') {
        return (
          <div className="border-2 border-dashed rounded-lg p-6 bg-muted/10">
            <div className="flex flex-col items-center justify-center text-center space-y-2">
              <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                <PenLine className="h-6 w-6 text-orange-500" />
              </div>
              <div className="text-sm font-medium text-muted-foreground">
                Signature Capture
              </div>
              <div className="text-xs text-muted-foreground">
                A signature pad will appear here when the workflow runs
              </div>
            </div>
          </div>
        );
      }

      return (
        <div className="space-y-2">
          <Input
            placeholder={`User will enter ${fieldName.toLowerCase()} here...`}
            disabled
            className="bg-muted/50"
          />
          <div className="text-xs text-muted-foreground italic">
            This field will be editable when the workflow runs
          </div>
        </div>
      );
    }

    return null;
  };

  // Special layout for Text content items
  if (item.label === 'Text' && item.category === 'content') {
    return (
      <>
        {showDropIndicator && dropIndicatorPosition === 'above' && (
          <div className="h-1 bg-primary rounded-full mb-2 animate-pulse" />
        )}
        <div
          ref={setNodeRef}
          style={style}
          className={`flex items-center gap-3 group mb-3 ${isDragging ? 'z-50 opacity-50' : ''}`}
        >
        {/* Drag handle on left */}
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground transition-colors touch-none opacity-0 group-hover:opacity-100"
        >
          <GripVertical className="h-4 w-4" />
        </button>

        {/* Content in middle */}
        <div className="flex-1 min-w-0">
          {renderContentEditor()}
        </div>

        {/* 3-dot menu on right - centered vertically, shows on hover */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="text-muted-foreground hover:text-foreground transition-colors opacity-0 group-hover:opacity-100">
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onDuplicate(item.id)}>
              <Copy className="h-4 w-4 mr-2" />
              Duplicate
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onRemove(item.id)}
              className="text-red-600 focus:text-red-600"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {showDropIndicator && dropIndicatorPosition === 'below' && (
        <div className="h-1 bg-primary rounded-full mt-1 mb-2 animate-pulse" />
      )}
      </>
    );
  }

  // Standard layout for other items
  return (
    <>
    {showDropIndicator && dropIndicatorPosition === 'above' && (
      <div className="h-1 bg-primary rounded-full mb-2 animate-pulse" />
    )}
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 group mb-3 ${isDragging ? 'z-50 opacity-50' : ''}`}
    >
      {/* Drag handle on left - centered vertically, shows on hover */}
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground transition-colors touch-none opacity-0 group-hover:opacity-100"
      >
        <GripVertical className="h-4 w-4" />
      </button>

      {/* Content in middle */}
      <div className="flex-1 min-w-0">
        {/* Header with label - only for form fields */}
        {item.category === 'form' && (
          <div className="flex items-center gap-2 mb-2">
            {Icon && typeof Icon === 'function' && <Icon className={`h-4 w-4 flex-shrink-0 ${item.color || ''}`} />}
            <span
              ref={(el) => {
                if (el) el._editableSpan = el;
              }}
              contentEditable
              suppressContentEditableWarning
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.currentTarget.blur();
                }
              }}
              onBlur={(e) => {
                const newName = e.currentTarget.textContent.trim();
                if (newName) {
                  onUpdateContent(item.id, { ...item.content, fieldName: newName });
                }
              }}
              className="font-medium text-sm outline-none cursor-text whitespace-nowrap overflow-hidden flex-1"
            >
              {item.content?.fieldName || item.label}
            </span>
            {/* Required indicator */}
            {item.content?.required && (
              <span className="text-red-500 text-sm font-medium" title="Required field">*</span>
            )}
            {/* Conditional logic indicator */}
            {hasConditions && (
              <button
                onClick={() => setShowConditionBuilder(true)}
                className="text-amber-500 hover:text-amber-600 transition-colors"
                title={`${item.conditionalLogic?.mode === 'show_when' ? 'Shows' : 'Hides'} when conditions are met`}
              >
                <Zap className="h-4 w-4" />
              </button>
            )}
            <button
              onClick={(e) => {
                const span = e.currentTarget.parentElement.querySelector('[contenteditable]');
                if (span) {
                  span.focus();
                  // Select all text
                  const range = document.createRange();
                  range.selectNodeContents(span);
                  const selection = window.getSelection();
                  selection.removeAllRanges();
                  selection.addRange(range);
                }
              }}
              className="text-muted-foreground hover:text-foreground transition-colors opacity-0 group-hover:opacity-100"
            >
              <Pencil className="h-3 w-3" />
            </button>
          </div>
        )}

        {/* Content area */}
        <div>
          {renderContentEditor()}
        </div>
      </div>

      {/* 3-dot menu on right - centered vertically, shows on hover */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="text-muted-foreground hover:text-foreground transition-colors opacity-0 group-hover:opacity-100">
            <MoreHorizontal className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {/* Required toggle - only for form fields */}
          {item.category === 'form' && (
            <>
              <DropdownMenuItem
                onClick={(e) => {
                  e.preventDefault();
                  onUpdateContent(item.id, { ...item.content, required: !item.content?.required });
                }}
              >
                <div className="flex items-center gap-2 w-full">
                  <div className={`h-4 w-4 border rounded flex items-center justify-center ${item.content?.required ? 'bg-primary border-primary' : 'border-muted-foreground'}`}>
                    {item.content?.required && <Check className="h-3 w-3 text-primary-foreground" />}
                  </div>
                  <span>Required</span>
                </div>
              </DropdownMenuItem>

              {/* Conditional Logic Submenu */}
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <Zap className={`h-4 w-4 mr-2 ${hasConditions ? 'text-amber-500' : ''}`} />
                  <span>Hide conditionally</span>
                  {hasConditions && (
                    <span className="ml-auto text-xs text-amber-500 font-medium">Active</span>
                  )}
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  <DropdownMenuRadioGroup
                    value={item.conditionalLogic?.mode || 'disabled'}
                    onValueChange={handleConditionModeChange}
                  >
                    <DropdownMenuRadioItem value="disabled">
                      Disabled (always show)
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="show_when">
                      Show when...
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="hide_when">
                      Hide when...
                    </DropdownMenuRadioItem>
                  </DropdownMenuRadioGroup>
                  {item.conditionalLogic?.mode && item.conditionalLogic.mode !== 'disabled' && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => setShowConditionBuilder(true)}>
                        <Pencil className="h-4 w-4 mr-2" />
                        Configure conditions
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuSubContent>
              </DropdownMenuSub>

              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuItem onClick={() => onDuplicate(item.id)}>
            <Copy className="h-4 w-4 mr-2" />
            Duplicate
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => onRemove(item.id)}
            className="text-red-600 focus:text-red-600"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>

    {showDropIndicator && dropIndicatorPosition === 'below' && (
      <div className="h-1 bg-primary rounded-full mt-1 mb-2 animate-pulse" />
    )}

    {/* Conditional Logic Builder Modal */}
    {item.category === 'form' && (
      <ConditionalLogicBuilder
        open={showConditionBuilder}
        onOpenChange={setShowConditionBuilder}
        currentField={item}
        tasks={tasks || []}
        currentTaskId={currentTaskId}
        conditionalLogic={item.conditionalLogic}
        onSave={handleSaveConditions}
      />
    )}
    </>
  );
}

// Droppable canvas area
export function DroppableCanvas({
  children,
  activeTaskContent,
  background,
  formTitle,
  onFormTitleChange,
  onOpenAssignment,
  assigneeCount,
  onOpenDueDate,
  hasDueDate,
  onToggleStop,
  hasStopEnabled,
  // Task-level conditional logic props
  tasks,
  currentTaskId,
  taskConditionalLogic,
  onUpdateTaskConditionalLogic,
  // AI Flow props
  onOpenAiFlow,
  hasAiFlow,
  // End screen mode
  isEndScreen
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: 'canvas-droppable',
  });

  // State for task-level conditional logic builder
  const [showTaskConditionBuilder, setShowTaskConditionBuilder] = useState(false);

  // Check if task has conditions
  const hasTaskConditions = taskConditionalLogic &&
    taskConditionalLogic.mode !== 'disabled' &&
    ((taskConditionalLogic.conditions?.length > 0 && taskConditionalLogic.conditions.some(c => c.fieldId)) ||
     (taskConditionalLogic.groups?.length > 0));

  // Handle saving task conditions
  const handleSaveTaskConditions = (conditionalLogic) => {
    onUpdateTaskConditionalLogic?.(conditionalLogic);
    setShowTaskConditionBuilder(false);
  };

  // Create a "fake" field object for the ConditionalLogicBuilder (task-level)
  const taskAsField = {
    id: currentTaskId,
    label: 'Task',
    conditionalLogic: taskConditionalLogic
  };

  // Build background style
  const backgroundStyle = background
    ? (background.type === 'image'
        ? { backgroundImage: `url(${background.value})`, backgroundSize: 'cover', backgroundPosition: 'center' }
        : { background: background.value })
    : {};

  return (
    <div
      className={`flex-1 rounded-lg p-6 transition-all duration-200 min-h-screen ${
        isOver ? 'ring-4 ring-primary/50 ring-offset-2' : ''
      } ${!background ? 'bg-gradient-to-br from-blue-100 via-blue-200 to-cyan-200' : ''}`}
      style={backgroundStyle}
    >
      <div
        className={`bg-white rounded-lg shadow-lg w-full max-w-2xl mx-auto min-h-[500px] transition-all duration-200 ${
          isOver ? 'ring-2 ring-primary/30 scale-[1.01]' : ''
        }`}
      >
        {/* Fixed Header Section */}
        <div className="p-8 pb-0">
          {/* Action Buttons Row - only for regular pages */}
          {!isEndScreen && (
            <div className="flex items-center gap-2 mb-6">
              <Button
                variant={assigneeCount > 0 ? "default" : "outline"}
                size="sm"
                className={`h-9 w-9 p-0 relative ${assigneeCount > 0 ? 'bg-black hover:bg-black/80 text-white' : ''}`}
                onClick={onOpenAssignment}
                title={assigneeCount > 0 ? `${assigneeCount} assignee${assigneeCount !== 1 ? 's' : ''} configured` : "Assign users"}
              >
                <User className="h-4 w-4" />
                {assigneeCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-5 w-5 bg-white text-black text-xs font-semibold rounded-full flex items-center justify-center border border-black">
                    {assigneeCount}
                  </span>
                )}
              </Button>
              <Button
                variant={hasDueDate ? "default" : "outline"}
                size="sm"
                className={`h-9 w-9 p-0 relative ${hasDueDate ? 'bg-black hover:bg-black/80 text-white' : ''}`}
                onClick={onOpenDueDate}
                title={hasDueDate ? "Due date configured" : "Set due date"}
              >
                <Calendar className="h-4 w-4" />
              </Button>
              <Button
                variant={hasStopEnabled ? "default" : "outline"}
                size="sm"
                className={`h-9 w-9 p-0 relative ${hasStopEnabled ? 'bg-black hover:bg-black/80 text-white' : ''}`}
                onClick={onToggleStop}
                title={hasStopEnabled ? "Stop enabled: Workflow will pause at this task until completed" : "Click to enable stop: Workflow will pause at this task"}
              >
                <Hand className="h-4 w-4" />
              </Button>
              <Button
                variant={hasTaskConditions ? "default" : "outline"}
                size="sm"
                className={`h-9 w-9 p-0 relative ${hasTaskConditions ? 'bg-black hover:bg-black/80 text-white' : ''}`}
                onClick={() => setShowTaskConditionBuilder(true)}
                title={hasTaskConditions ? "Task has conditional logic configured" : "Add conditional logic to show/hide this task"}
              >
                <Shuffle className="h-4 w-4" />
              </Button>
            </div>
          )}

          {/* End Screen Badge */}
          {isEndScreen && (
            <div className="flex items-center gap-2 mb-4">
              <div className="px-2.5 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium flex items-center gap-1.5">
                <FlagTriangleRight className="h-3 w-3" />
                End Screen
              </div>
            </div>
          )}

          {/* Form Title */}
          <Input
            value={formTitle || ''}
            onChange={(e) => onFormTitleChange?.(e.target.value)}
            placeholder={isEndScreen ? "End screen title..." : "Form Title"}
            className="font-bold border-0 px-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 mb-6 h-auto py-1"
            style={{ fontSize: '2rem', lineHeight: '2.5rem' }}
          />

          {/* Divider Line */}
          <div className={`border-t mb-6 ${isEndScreen ? 'border-emerald-200' : ''}`} />
        </div>

        {/* Droppable Content Area */}
        <div ref={setNodeRef} className="px-8 pb-8">
          <div className="space-y-3">
            {children}
          </div>
        </div>
      </div>

      {/* Task-level Conditional Logic Builder - not used for end screens */}
      {!isEndScreen && (
        <ConditionalLogicBuilder
          open={showTaskConditionBuilder}
          onOpenChange={setShowTaskConditionBuilder}
          currentField={taskAsField}
          tasks={tasks}
          currentTaskId={currentTaskId}
          conditionalLogic={taskConditionalLogic}
          onSave={handleSaveTaskConditions}
          isTaskLevel={true}
        />
      )}
    </div>
  );
}

// AI Flow Configuration Modal
export function AiFlowModal({ open, onOpenChange, aiFlowConfig, onSave, companyId }) {
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isConfigured, setIsConfigured] = useState(false);
  const [selectedFlowId, setSelectedFlowId] = useState(aiFlowConfig?.flowId || '');
  const [mustWait, setMustWait] = useState(aiFlowConfig?.mustWait ?? false);
  const [enabled, setEnabled] = useState(aiFlowConfig?.enabled ?? true);

  // Load AI Flows integration status and flows
  useEffect(() => {
    if (!open || !companyId) return;

    const loadFlows = async () => {
      setLoading(true);
      setError(null);

      try {
        const { getIntegration, getFlows } = await import('@/lib/aiFlowsService');

        // Check if integration is configured
        const integrationResult = await getIntegration(companyId);
        setIsConfigured(integrationResult.isConfigured);

        if (integrationResult.isConfigured) {
          // Fetch available flows
          const flowsData = await getFlows(companyId);
          setFlows(flowsData || []);
        }
      } catch (err) {
        console.error('Error loading AI Flows:', err);
        setError(err.message || 'Failed to load AI Flows');
      } finally {
        setLoading(false);
      }
    };

    loadFlows();
  }, [open, companyId]);

  // Update local state when aiFlowConfig changes
  useEffect(() => {
    setSelectedFlowId(aiFlowConfig?.flowId || '');
    setMustWait(aiFlowConfig?.mustWait ?? false);
    setEnabled(aiFlowConfig?.enabled ?? true);
  }, [aiFlowConfig]);

  const handleSave = () => {
    if (!selectedFlowId) {
      // Clear config if no flow selected
      onSave(null);
    } else {
      const selectedFlow = flows.find(f => f.id === selectedFlowId);
      onSave({
        flowId: selectedFlowId,
        flowName: selectedFlow?.name || 'Unknown Flow',
        webhookUrl: selectedFlow?.webhook_url || selectedFlow?.webhookUrl,
        mustWait: mustWait,
        enabled: enabled
      });
    }
    onOpenChange(false);
  };

  const handleRemove = () => {
    onSave(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-blue-500" />
            AI Flow Automation
          </DialogTitle>
          <DialogDescription>
            Trigger an AI Flow when this page is completed
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Loading AI Flows...</span>
            </div>
          ) : error ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : !isConfigured ? (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>AI Flows Not Configured</AlertTitle>
              <AlertDescription>
                AI Flows integration is not set up for your company.
                Go to Company Settings to configure the integration.
              </AlertDescription>
            </Alert>
          ) : (
            <>
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between py-2">
                <div>
                  <Label className="text-base">Enable AI Flow</Label>
                  <p className="text-sm text-muted-foreground">
                    When enabled, the selected flow will trigger on page completion
                  </p>
                </div>
                <Switch
                  checked={enabled}
                  onCheckedChange={setEnabled}
                />
              </div>

              <Separator />

              {/* Flow Selection */}
              <div className="space-y-2">
                <Label>Select AI Flow</Label>
                <AsyncCombobox
                  value={selectedFlowId}
                  onValueChange={setSelectedFlowId}
                  displayLabel={selectedFlowId ? flows.find(f => f.id === selectedFlowId)?.name : undefined}
                  placeholder="Search flows..."
                  emptyText="No flows found."
                  disabled={!enabled}
                  debounceMs={150}
                  onSearch={async (query) => {
                    const filtered = query
                      ? flows.filter(f => f.name.toLowerCase().includes(query.toLowerCase()))
                      : flows;
                    return filtered.map(f => ({ value: f.id, label: f.name }));
                  }}
                />
              </div>

              {/* Must Wait Option */}
              {selectedFlowId && (
                <div className="flex items-center justify-between py-2 border rounded-lg px-3">
                  <div>
                    <Label className="text-base flex items-center gap-2">
                      <Loader2 className="h-4 w-4" />
                      Must Wait
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      User will see a loading screen until the flow completes
                    </p>
                  </div>
                  <Switch
                    checked={mustWait}
                    onCheckedChange={setMustWait}
                    disabled={!enabled}
                  />
                </div>
              )}

              {/* Selected Flow Info */}
              {selectedFlowId && (
                <div className="p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-2 text-sm">
                    <Zap className="h-4 w-4 text-blue-500" />
                    <span className="font-medium">
                      {flows.find(f => f.id === selectedFlowId)?.name || 'Selected Flow'}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {mustWait
                      ? 'User will wait for flow completion before proceeding'
                      : 'Flow will run in background, user proceeds immediately'
                    }
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          {aiFlowConfig?.flowId && (
            <Button variant="destructive" onClick={handleRemove} className="mr-auto">
              <Trash2 className="h-4 w-4 mr-2" />
              Remove
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!isConfigured}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
